/** 한 번에 고른 파일 묶음을 **받을 것과 거절할 것으로 가른다.**
 *
 * ## ⭐ 왜 화면 밖으로 꺼냈나 (2026-09-24)
 *
 * 이 판정이 `UploadPanel.tsx` 의 `accept()` 안에 있던 동안 **시험을 걸 수가 없었다.** 그
 * 파일은 React 컴포넌트라 `react` 를 끌어오는데, 이 저장소의 화면 시험
 * (`scripts/*.test.mjs` → CI `gate.yml`)은 **`pnpm install` 없이 `node:` 빌트인만으로 돈다.**
 *
 * **막는 장치를 세워 놓고 그 장치를 붙잡아 두는 것을 안 세우면 다음 정리 회차에 조용히
 * 사라진다** — `A-31c` 회차가 그렇게 당했다(`scripts/result-gate.test.mjs` 머리말).
 * ⛔ 그러니 이 판정을 화면 안으로 되돌리지 마라. 되돌리는 순간 웹의 장수 상한은 **소스에
 * 글자만 남은 채로** 지켜지지 않게 된다.
 *
 * ## 거르는 순서가 뜻을 갖는다
 *
 * **형식·용량이 먼저고 자리가 나중이다.** 반대로 하면 **못 올릴 파일이 올릴 수 있는 파일의
 * 자리를 먹는다** — 20장 중 5장이 HEIC 인 셀러가 15장을 받아야 하는데 10장만 받게 된다.
 * (`packages/shared/src/upload.ts` 의 `acceptWithinImageLimit` 머리말이 같은 말을 한다.)
 *
 * ⛔ **자리를 `MAX_IMAGES_PER_JOB` 으로 세지 마라.** 이 묶음이 **두 번째 배치**일 수 있다 —
 * 웹은 끌어다 놓기를 여러 번 받고 큐에 이어붙인다. 이미 든 장을 안 빼면 2차 배치가 15장을
 * 또 받아들여 서버가 409 로 거절한다.
 */

import {
  DEFAULT_MAX_BYTES,
  checkUploadCandidate,
  remainingImageSlots,
  type UploadBlockReason,
  type UploadItemState,
} from "@makit/shared";

/** 큐의 행들 중 **이 작업의 자리를 차지한 수.**
 *
 * ⛔ **줄 수를 그대로 세지 마라.** 거절된 행(`REJECTED`)은 서버에 간 적이 없으므로 자리를
 * 안 먹는다. 그대로 세면 HEIC 다섯 장을 섞어 놓은 셀러가 **15장을 못 채운다** — 화면이
 * 자기가 거절한 장을 자기 자리로 세는 꼴이다.
 *
 * ⚠️ `duplicate` 인 행은 **센다.** 서버가 해시로 잡은 중복은 이미 그 작업에 행이 있다는
 * 뜻이라 자리를 하나 쓰고 있다. 금액에서 빼는 것(`chargeable`)과 자리를 세는 것은 축이
 * 다르다 — 중복은 **돈은 안 늘고 자리는 이미 찬** 장이다. */
export function takenSlots(states: readonly UploadItemState[]): number {
  return states.filter((state) => state !== "REJECTED").length;
}

export interface BatchCandidate {
  contentType: string;
  byteSize: number;
}

/** 한 파일의 판정. `reason` 이 없으면 받는다. */
export interface BatchVerdict {
  /** 넘긴 배열에서의 자리. **순서를 보존한다** — 고른 순서가 곧 상세 순서다. */
  index: number;
  accepted: boolean;
  /** 거절 사유. `uploadRejectMessage` 가 이 값을 문장으로 바꾼다. */
  reason?: UploadBlockReason;
}

export interface BatchPlan {
  verdicts: BatchVerdict[];
  /** 받기로 한 장 수. */
  accepted: number;
  /** **상한이 차서** 밀려난 장 수. 형식·용량 거절과 **다른 수**다(화면이 다른 문장으로 말한다). */
  full: number;
}

/**
 * @param candidates 이번에 고른 파일들. 순서 그대로.
 * @param takenCount **이미 이 작업의 자리를 차지한 장 수.** 큐에 있는 것과 올라간 것을
 *   합친 수이고, 거절된 행은 빼야 한다 — 그 행은 서버에 가지 않으므로 자리를 안 먹는다.
 */
export function planUploadBatch(
  candidates: readonly BatchCandidate[],
  takenCount: number,
  maxBytes: number = DEFAULT_MAX_BYTES,
): BatchPlan {
  let room = remainingImageSlots(takenCount);
  const verdicts: BatchVerdict[] = [];
  let accepted = 0;
  let full = 0;

  for (const [index, candidate] of candidates.entries()) {
    const reject = checkUploadCandidate(
      { contentType: candidate.contentType, byteSize: candidate.byteSize },
      maxBytes,
    );
    if (reject) {
      verdicts.push({ index, accepted: false, reason: reject });
      continue;
    }
    if (room <= 0) {
      verdicts.push({ index, accepted: false, reason: "JOB_FULL" });
      full += 1;
      continue;
    }
    room -= 1;
    accepted += 1;
    verdicts.push({ index, accepted: true });
  }

  return { verdicts, accepted, full };
}
